import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Flex from 'shared/ui/Flex'
import ChiliImages, {
  ChiliImagesEventTypes
} from 'shared/components/Tools/services/ChiliImages'
import { DocumentImageFrame } from 'shared/components/Tools/services/types'
import { ImageFrame } from 'shared/components/Tools/types'
import { getQualityColor, PrintQuality } from 'shared/utils/imageQuality'

const imagesApi = ChiliImages.getInstance()

type ImageQualityBadgeProps = {
  frame: ImageFrame
}

const Container = styled(Flex)<{ $badgeColor: string }>`
  padding: 12px 16px;
  background: ${({ $badgeColor }) => `${$badgeColor}15`};
  border-left: 4px solid ${({ $badgeColor }) => $badgeColor};
  border-radius: 6px;
  margin-top: 16px;
  margin-bottom: 8px;
`

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.base.c10};
  margin-bottom: 4px;
`

const Info = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.base.c6};
  line-height: 1.4;
`

const DPIValue = styled.span<{ $badgeColor: string }>`
  font-weight: 600;
  color: ${({ $badgeColor }) => $badgeColor};
`

const Warning = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.color.base.c6};
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.color.base.c2};
`

const getQualityEmoji = (quality: string | undefined): string => {
  switch (quality) {
    case PrintQuality.EXCELLENT:
      return '✨'
    case PrintQuality.GOOD:
      return '✓'
    case PrintQuality.ACCEPTABLE:
      return '⚠️'
    case PrintQuality.POOR:
      return '⚠️'
    default:
      return '📊'
  }
}

const getQualityMessage = (
  quality: string | undefined,
  formatMessage: any
): string => {
  switch (quality) {
    case PrintQuality.EXCELLENT:
      return formatMessage({ id: 'Panel.image.quality.excellent' })
    case PrintQuality.GOOD:
      return formatMessage({ id: 'Panel.image.quality.good' })
    case PrintQuality.ACCEPTABLE:
      return formatMessage({ id: 'Panel.image.quality.acceptable' })
    case PrintQuality.POOR:
      return formatMessage({ id: 'Panel.image.quality.poor' })
    default:
      return formatMessage({ id: 'Panel.image.quality.calculating' })
  }
}

const ImageQualityBadge: React.FC<ImageQualityBadgeProps> = ({ frame }) => {
  const { formatMessage } = useIntl()
  const [frameData, setFrameData] = useState<DocumentImageFrame | null>(
    imagesApi.getFrame(frame.id)
  )

  useEffect(() => {
    setFrameData(imagesApi.getFrame(frame.id))
  }, [frame.id])

  useEffect(() => {
    const updateListener = imagesApi.addListener(
      ChiliImagesEventTypes.frameUpdated,
      () => {
        const currentFrame = imagesApi.getFrame(frame.id)
        if (currentFrame) {
          setFrameData({ ...currentFrame })
        }
      }
    )

    return () => {
      imagesApi.removeListener(updateListener)
    }
  }, [frame.id])

  if (!frameData || !frameData.effectiveDPI) {
    return null
  }

  const quality = frameData.printQuality
  const color = quality
    ? getQualityColor(quality as PrintQuality)
    : '#6B7280'
  const emoji = getQualityEmoji(quality)

  return (
    <Container $badgeColor={color} direction="column">
      <Title>
        {emoji} {getQualityMessage(quality, formatMessage)}
      </Title>
      <Info>
        {formatMessage({ id: 'Panel.image.quality.dpi' })}:{' '}
        <DPIValue $badgeColor={color}>
          {frameData.effectiveDPI} DPI
        </DPIValue>
      </Info>
      {quality === PrintQuality.ACCEPTABLE && (
        <Warning>
          {formatMessage({
            id: 'Panel.image.quality.warning.acceptable'
          })}
        </Warning>
      )}
      {quality === PrintQuality.POOR && (
        <Warning>
          {formatMessage({
            id: 'Panel.image.quality.warning.poor'
          })}
        </Warning>
      )}
    </Container>
  )
}

export default ImageQualityBadge
